import _ from "lodash";
import {FormEvent, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Pref, UserPref, useSetTenantPrefMutation} from "../../../../generated/graphql";
import {FeatureFlagContext} from "../../../../providers/FeatureFlagProvider";
import {extractSimplePref, PreferenceContext, PrefValue} from "../../../../providers/PreferenceProvider";
import {TenantPreferences} from "../../../../constants/constants";
import {ManifestStatusType} from "../../ColorSettings/ColorizedIndicators/types/manifestAttributes";
import {DriverRequirementEnforcementLevel} from "../../enums/DriverRequirementEnforcementLevel";
import {OrderEntryType} from "../../enums/OrderEntry";
import "../common/CompanySettings.css";
import {ContentProps} from "../common/components/Content";
import Section from "../common/components/Section";
import {
  ContentNumericProp,
  ContentOptionProps,
  DEFAULT_DWELL_MINUTES,
  DEFAULT_ROUTE_PLANNING_LIMIT,
  DEFAULT_SUGGESTED_ASSIGNMENTS_PER_DAY,
  MAX_DWELL_MINUTES,
  MIN_DWELL_MINUTES,
  MIN_SUGGESTED_ASSIGNMENTS_PER_DAY,
  RoutePlanLimitMode
} from "../common/constants";
import {renderNumericTextBox, renderRadioGroupProps} from "../common/renderer";
import {Body, CancelButton, Container, Footer, Header, HeaderText, SaveButton} from "../common/styles";
import log from "loglevel";
import {OptimizationObjective} from "../../enums/RoutePlanning";
import {useAuthState} from "../../../../providers/AuthProvider";

const General = () => {
  const {tenantPreferences, tenantPrefsQueryRefetch} = useContext(PreferenceContext);
  const {isSuperUser} = useAuthState();
  const [setTenantPref, {loading: isSetTenantPrefLoading, data: setTenantPrefData}] = useSetTenantPrefMutation();
  const {
    driverRequirements: driverRequirementsEnabled,
    idexxLogicCompleteStops: idexxFeatureEnabled,
    askManifestCheckoutInfo: askManifestCheckoutInfoEnabled,
    skills: skillsEnabled,
    routePlanning: routePlanningEnabled,
    closedNeverReassign: closedNeverReassignEnabled,
    itineraryPlanningSkills: itineraryPlanningSkillsEnabled,
    suggestedAssignment: suggestedAssignmentEnabled
  } = useContext(FeatureFlagContext);

  const [orderEntryType, setOrderEntryType] = useState<OrderEntryType>(OrderEntryType.Current);
  const [initOrderEntryType, setInitOrderEntryType] = useState<OrderEntryType | null>(null);

  const [isDispatcherIgnoreTimeWindows, setDispatcherIgnoreTimeWindows] = useState<boolean>(false);
  const [initDispatcherIgnoreTimeWindows, setInitDispatcherIgnoreTimeWindows] = useState<boolean>(false);

  const [isDispatcherIgnoreTimeWindowsOnFirstTry, setDispatcherIgnoreTimeWindowsOnFirstTry] = useState<boolean>(true);
  const [initDispatcherIgnoreTimeWindowsOnFirstTry, setInitDispatcherIgnoreTimeWindowsOnFirstTry] =
    useState<boolean>(false);

  const [isDispatcherIgnoreTimeWindowsAsDefault, setDispatcherIgnoreTimeWindowsAsDefault] = useState<boolean>(false);
  const [initDispatcherIgnoreTimeWindowsAsDefault, setInitDispatcherIgnoreTimeWindowsAsDefault] =
    useState<boolean>(false);

  const [driverIgnoreTimeWindows, setDriverIgnoreTimeWindows] = useState<boolean>(false);
  const [initDriverIgnoreTimeWindows, setInitDriverIgnoreTimeWindows] = useState<boolean>(false);

  const [assumedOptimizationDwellTime, setAssumedOptimizationDwellTime] = useState<number | undefined>(undefined);
  const [initAssumedOptimizationDwellTime, setInitAssumedOptimizationDwellTime] = useState<number | undefined>(
    undefined
  );

  const [timezoneOfJobStop, setTimezoneOfJobStop] = useState<boolean>(false);
  const [initTimezoneOfJobStop, setInitTimezoneOfJobStop] = useState<boolean>(false);

  const [requireArrAndDepTimes, setRequireArrAndDepTimes] = useState<boolean>(false);
  const [initRequireArrAndDepTimes, setInitRequireArrAndDepTimes] = useState<boolean>(false);

  const [canUnassignPartialJob, setCanUnassignPartialJob] = useState<boolean>(false);
  const [initCanUnassignPartialJob, setInitCanUnassignPartialJob] = useState<boolean>(false);

  const [defaultManifestStatus, setDefaultManifestStatus] = useState<ManifestStatusType | null>(null);
  const [initDefaultManifestStatus, setInitDefaultManifestStatus] = useState<ManifestStatusType | null>(null);

  const [idexxCompleteStopLogic, setIdexxCompleteStopLogic] = useState<boolean>(false);
  const [initIdexxCompleteStopLogic, setInitIdexxCompleteStopLogic] = useState<boolean>(false);

  const [askCheckoutTimeAndMileage, setAskCheckoutTimeAndMileage] = useState<boolean>(false);
  const [initAskCheckoutTimeAndMileage, setInitAskCheckoutTimeAndMileage] = useState<boolean>(false);

  const [restrictStationVisibility, setRestrictStationVisibility] = useState<boolean>(false);
  const [initRestrictStationVisibility, setInitRestrictStationVisibility] = useState<boolean>(false);

  const [skillBasedRequirements, setSkillBasedRequirements] = useState<boolean>(false);
  const [initSkillBasedRequirements, setInitSkillBasedRequirements] = useState<boolean>(false);

  // DP-3166: unset companies default to "Yes" — both initials must match so the form is not dirty on load
  const [considerSkillsRoutePlanning, setConsiderSkillsRoutePlanning] = useState<boolean>(true);
  const [initConsiderSkillsRoutePlanning, setInitConsiderSkillsRoutePlanning] = useState<boolean>(true);

  const [enforcementLevel, setEnforcementLevel] = useState<DriverRequirementEnforcementLevel>(
    DriverRequirementEnforcementLevel.Warning
  );
  const [initEnforcementLevel, setInitEnforcementLevel] = useState<DriverRequirementEnforcementLevel | null>(null);

  const [createManifestOutSide, setCreateManifestOutSide] = useState<boolean>(false);
  const [initCreateManifestOutSide, setInitCreateManifestOutSide] = useState<boolean>(false);

  // DP-3166: unset companies default to "Yes" — both initials must match so the form is not dirty on load
  const [dismissFailedJobsRoutePlanning, setDismissFailedJobsRoutePlanning] = useState<boolean>(true);
  const [initDismissFailedJobsRoutePlanning, setInitDismissFailedJobsRoutePlanning] = useState<boolean>(true);

  // DP-3113: default the "Optimization objectives" setting to "Both" for companies that never set it.
  const [optimizationObjectives, setOptimizationObjectives] = useState<OptimizationObjective>(
    OptimizationObjective.Both
  );
  const [initOptimizationObjectives, setInitOptimizationObjectives] = useState<OptimizationObjective>(
    OptimizationObjective.Both
  );
  const [stopCompletedAtFutureTime, setStopCompletedAtFutureTime] = useState<boolean>(false);
  const [initStopCompletedAtFutureTime, setInitStopCompletedAtFutureTime] = useState<boolean>(false);

  const [closedNeverReassign, setClosedNeverReassign] = useState<boolean>(false);
  const [initClosedNeverReassign, setInitClosedNeverReassign] = useState<boolean>(false);

  const [routePlanLimitMode, setRoutePlanLimitMode] = useState<RoutePlanLimitMode>(RoutePlanLimitMode.Limited);
  const [initRoutePlanLimitMode, setInitRoutePlanLimitMode] = useState<RoutePlanLimitMode>(RoutePlanLimitMode.Limited);
  const [routePlanningLimit, setRoutePlanningLimit] = useState<number | undefined>(DEFAULT_ROUTE_PLANNING_LIMIT);
  const [initRoutePlanningLimit, setInitRoutePlanningLimit] = useState<number | undefined>(
    DEFAULT_ROUTE_PLANNING_LIMIT
  );

  // DP-3194: daily suggested assignment cap
  const [suggestedAssignmentsPerDay, setSuggestedAssignmentsPerDay] = useState<number>(
    DEFAULT_SUGGESTED_ASSIGNMENTS_PER_DAY
  );
  const [initSuggestedAssignmentsPerDay, setInitSuggestedAssignmentsPerDay] = useState<number>(
    DEFAULT_SUGGESTED_ASSIGNMENTS_PER_DAY
  );

  const updateTenantPrefs = useCallback(
    (key: string, value: any) => {
      setTenantPref({
        variables: {
          name: key,
          input: {
            value: JSON.stringify({value: value})
          }
        }
      })
        .then(() => {
          if (tenantPrefsQueryRefetch) tenantPrefsQueryRefetch();
        })
        .catch((error: Error) => {
          log.error(error);
        });
    },
    [setTenantPref, tenantPrefsQueryRefetch]
  );

  // OnChange Handlers
  const handleDriverRequiredChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = event.currentTarget.value as DriverRequirementEnforcementLevel;
    setEnforcementLevel(selectedValue);
  }, []);

  const handleSkillBasedRequirementsChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setSkillBasedRequirements(selectedValue);
  }, []);

  const handleConsiderSkillsRoutePlanningChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setConsiderSkillsRoutePlanning(selectedValue);
  }, []);

  const handleOrderEntryTypeChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = event.currentTarget.value as OrderEntryType;
    setOrderEntryType(selectedValue);
  }, []);

  const handleDispatcherIgnoreTimeWindowsChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setDispatcherIgnoreTimeWindows(selectedValue);
  }, []);

  const handleDispatcherIgnoreTimeWindowsOnFirstTryChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setDispatcherIgnoreTimeWindowsOnFirstTry(selectedValue);
  }, []);

  const handleDispatcherIgnoreTimeWindowsAsDefaultChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setDispatcherIgnoreTimeWindowsAsDefault(selectedValue);
  }, []);

  const handleDriverIgnoreTimeWindowsChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setDriverIgnoreTimeWindows(selectedValue);
  }, []);

  const handleAssumedOptimizationDwellTimeChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    let selectedValue: number | undefined = parseInt(event.currentTarget.value);
    if (isNaN(selectedValue) || selectedValue < 0) {
      selectedValue = 0;
    }
    if (selectedValue > MAX_DWELL_MINUTES) {
      return;
    }
    setAssumedOptimizationDwellTime(selectedValue);
  }, []);

  const handleAssumedOptimizationDwellTimeBlur = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      if (Number((event.target as HTMLInputElement).value) < MIN_DWELL_MINUTES) {
        handleAssumedOptimizationDwellTimeChange({
          currentTarget: {value: MIN_DWELL_MINUTES.toString()} as HTMLInputElement
        } as FormEvent<HTMLInputElement>);
      }
    },
    [handleAssumedOptimizationDwellTimeChange]
  );

  const handleTimezoneOfJobStopChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setTimezoneOfJobStop(selectedValue);
  }, []);

  const handleRequireArrAndDepTimesChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setRequireArrAndDepTimes(selectedValue);
  }, []);

  const handleCanUnassignPartialJobChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setCanUnassignPartialJob(selectedValue);
  }, []);

  const handleDefaultManifestStatusChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = event.currentTarget.value;
    setDefaultManifestStatus(selectedValue as ManifestStatusType);
  }, []);

  const handleIdexxCompleteStopLogicChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setIdexxCompleteStopLogic(selectedValue);
  }, []);

  const handleAskCheckoutTimeAndMilageChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setAskCheckoutTimeAndMileage(selectedValue);
  }, []);

  const handleRestrictStationVisibilityChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setRestrictStationVisibility(selectedValue);
  }, []);

  const handleCreateManifestOutSideChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setCreateManifestOutSide(selectedValue);
  }, []);

  const handleDismissFailedJobsRoutePlanningChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setDismissFailedJobsRoutePlanning(selectedValue);
  }, []);

  const handleOptimizationObjectivesChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    setOptimizationObjectives(event.currentTarget.value as OptimizationObjective);
  }, []);

  const handleStopCompletedAtFutureTimeChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setStopCompletedAtFutureTime(selectedValue);
  }, []);

  const handleClosedNeverReassignChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setClosedNeverReassign(selectedValue);
  }, []);

  const handleRoutePlanLimitModeChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    setRoutePlanLimitMode(event.currentTarget.value as RoutePlanLimitMode);
  }, []);

  const handleRoutePlanningLimitChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    // Floor at 0 (a Limited cap of 0 = no plans allowed); negatives/blanks reset to 0.
    let selectedValue: number | undefined = parseInt(event.currentTarget.value);
    if (isNaN(selectedValue) || selectedValue < 0) {
      selectedValue = 0;
    }
    setRoutePlanningLimit(selectedValue);
  }, []);

  // DP-3194: suggested assignment daily cap
  const handleSuggestedAssignmentsPerDayChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    let selectedValue = parseInt(event.currentTarget.value);
    if (isNaN(selectedValue) || selectedValue < MIN_SUGGESTED_ASSIGNMENTS_PER_DAY) {
      selectedValue = MIN_SUGGESTED_ASSIGNMENTS_PER_DAY;
    }
    setSuggestedAssignmentsPerDay(selectedValue);
  }, []);

  //Dirty Checks
  const isEnforcementLevelDirty = useCallback(() => {
    return initEnforcementLevel !== enforcementLevel;
  }, [enforcementLevel, initEnforcementLevel]);

  const isSkillBasedRequirementsDirty = useCallback(() => {
    return initSkillBasedRequirements !== skillBasedRequirements;
  }, [initSkillBasedRequirements, skillBasedRequirements]);

  const isConsiderSkillsRoutePlanningDirty = useCallback(() => {
    return initConsiderSkillsRoutePlanning !== considerSkillsRoutePlanning;
  }, [initConsiderSkillsRoutePlanning, considerSkillsRoutePlanning]);

  const isOrderEntryTypeDirty = useCallback(() => {
    return initOrderEntryType !== orderEntryType;
  }, [initOrderEntryType, orderEntryType]);

  const isDispatcherIgnoreTimeWindowsDirty = useCallback(() => {
    return initDispatcherIgnoreTimeWindows !== isDispatcherIgnoreTimeWindows;
  }, [initDispatcherIgnoreTimeWindows, isDispatcherIgnoreTimeWindows]);

  const isDispatcherIgnoreTimeWindowsOnFirstTryDirty = useCallback(() => {
    return initDispatcherIgnoreTimeWindowsOnFirstTry !== isDispatcherIgnoreTimeWindowsOnFirstTry;
  }, [initDispatcherIgnoreTimeWindowsOnFirstTry, isDispatcherIgnoreTimeWindowsOnFirstTry]);

  const isDispatcherIgnoreTimeWindowsAsDefaultDirty = useCallback(() => {
    return initDispatcherIgnoreTimeWindowsAsDefault !== isDispatcherIgnoreTimeWindowsAsDefault;
  }, [initDispatcherIgnoreTimeWindowsAsDefault, isDispatcherIgnoreTimeWindowsAsDefault]);

  const isDriverIgnoreTimeWindowsDirty = useCallback(() => {
    return initDriverIgnoreTimeWindows !== driverIgnoreTimeWindows;
  }, [initDriverIgnoreTimeWindows, driverIgnoreTimeWindows]);

  const isDriverAverageDwellTimeDirty = useCallback(() => {
    return initAssumedOptimizationDwellTime !== assumedOptimizationDwellTime;
  }, [initAssumedOptimizationDwellTime, assumedOptimizationDwellTime]);

  const isTimezoneOfJobStopDirty = useCallback(() => {
    return initTimezoneOfJobStop !== timezoneOfJobStop;
  }, [initTimezoneOfJobStop, timezoneOfJobStop]);

  const isRequireArrAndDepTimesDirty = useCallback(() => {
    return initRequireArrAndDepTimes !== requireArrAndDepTimes;
  }, [initRequireArrAndDepTimes, requireArrAndDepTimes]);

  const isCanUnassignPartialJobDirty = useCallback(() => {
    return initCanUnassignPartialJob !== canUnassignPartialJob;
  }, [canUnassignPartialJob, initCanUnassignPartialJob]);

  const isDefaultManifestStatusDirty = useCallback(() => {
    return initDefaultManifestStatus !== defaultManifestStatus;
  }, [defaultManifestStatus, initDefaultManifestStatus]);

  const isIdexxCompleteStopLogicDirty = useCallback(() => {
    return initIdexxCompleteStopLogic !== idexxCompleteStopLogic;
  }, [idexxCompleteStopLogic, initIdexxCompleteStopLogic]);

  const isAskCheckoutTimeAndMileageDirty = useCallback(() => {
    return initAskCheckoutTimeAndMileage !== askCheckoutTimeAndMileage;
  }, [askCheckoutTimeAndMileage, initAskCheckoutTimeAndMileage]);

  const isRestrictStationVisibilityDirty = useCallback(() => {
    return initRestrictStationVisibility !== restrictStationVisibility;
  }, [initRestrictStationVisibility, restrictStationVisibility]);

  const isCreateManifestOutSideDirty = useCallback(() => {
    return initCreateManifestOutSide !== createManifestOutSide;
  }, [initCreateManifestOutSide, createManifestOutSide]);

  const isDismissFailedJobsRoutePlanningDirty = useCallback(() => {
    return initDismissFailedJobsRoutePlanning !== dismissFailedJobsRoutePlanning;
  }, [initDismissFailedJobsRoutePlanning, dismissFailedJobsRoutePlanning]);

  const isOptimizationObjectivesDirty = useCallback(() => {
    return optimizationObjectives !== initOptimizationObjectives;
  }, [optimizationObjectives, initOptimizationObjectives]);

  const isStopCompletedAtFutureTimeDirty = useCallback(() => {
    return initStopCompletedAtFutureTime !== stopCompletedAtFutureTime;
  }, [initStopCompletedAtFutureTime, stopCompletedAtFutureTime]);

  const isClosedNeverReassignDirty = useCallback(() => {
    return initClosedNeverReassign !== closedNeverReassign;
  }, [initClosedNeverReassign, closedNeverReassign]);

  const isRoutePlanLimitDirty = useCallback(() => {
    if (initRoutePlanLimitMode !== routePlanLimitMode) {
      return true;
    }
    if (routePlanLimitMode === RoutePlanLimitMode.Limited) {
      return initRoutePlanningLimit !== routePlanningLimit;
    }
    return false;
  }, [initRoutePlanLimitMode, routePlanLimitMode, initRoutePlanningLimit, routePlanningLimit]);

  const isSuggestedAssignmentsPerDayDirty = useCallback(() => {
    return initSuggestedAssignmentsPerDay !== suggestedAssignmentsPerDay;
  }, [initSuggestedAssignmentsPerDay, suggestedAssignmentsPerDay]);

  const isSaveChange = () => {
    return (
      isEnforcementLevelDirty() ||
      isOrderEntryTypeDirty() ||
      isDispatcherIgnoreTimeWindowsDirty() ||
      isDispatcherIgnoreTimeWindowsOnFirstTryDirty() ||
      isDispatcherIgnoreTimeWindowsAsDefaultDirty() ||
      isDriverIgnoreTimeWindowsDirty() ||
      isDriverAverageDwellTimeDirty() ||
      isTimezoneOfJobStopDirty() ||
      isRequireArrAndDepTimesDirty() ||
      isCanUnassignPartialJobDirty() ||
      isDefaultManifestStatusDirty() ||
      isIdexxCompleteStopLogicDirty() ||
      isAskCheckoutTimeAndMileageDirty() ||
      isRestrictStationVisibilityDirty() ||
      isCreateManifestOutSideDirty() ||
      isSkillBasedRequirementsDirty() ||
      isConsiderSkillsRoutePlanningDirty() ||
      isDismissFailedJobsRoutePlanningDirty() ||
      isOptimizationObjectivesDirty() ||
      isStopCompletedAtFutureTimeDirty() ||
      isClosedNeverReassignDirty() ||
      isRoutePlanLimitDirty() ||
      isSuggestedAssignmentsPerDayDirty()
    );
  };

  const handleSaveSetting = useCallback(() => {
    if (isEnforcementLevelDirty()) {
      updateTenantPrefs(TenantPreferences.driverRequirementEnforcement, enforcementLevel);
    }
    if (isOrderEntryTypeDirty()) {
      updateTenantPrefs(TenantPreferences.orderEntryType, orderEntryType);
    }
    if (isDispatcherIgnoreTimeWindowsDirty()) {
      updateTenantPrefs(TenantPreferences.dispatcherIgnoreTimeWindows, isDispatcherIgnoreTimeWindows);
    }
    if (isDispatcherIgnoreTimeWindowsOnFirstTryDirty()) {
      updateTenantPrefs(
        TenantPreferences.dispatcherIgnoreTimeWindowsOnFirstTry,
        isDispatcherIgnoreTimeWindowsOnFirstTry
      );
    }
    if (isDispatcherIgnoreTimeWindowsAsDefaultDirty()) {
      updateTenantPrefs(TenantPreferences.dispatcherIgnoreTimeWindowsAsDefault, isDispatcherIgnoreTimeWindowsAsDefault);
    }
    if (isDriverIgnoreTimeWindowsDirty()) {
      updateTenantPrefs(TenantPreferences.driverIgnoreTimeWindows, driverIgnoreTimeWindows);
      if (driverIgnoreTimeWindows && !assumedOptimizationDwellTime) {
        updateTenantPrefs(TenantPreferences.assumedOptimizationDwellTime, DEFAULT_DWELL_MINUTES);
      }
    }
    if (isDriverAverageDwellTimeDirty()) {
      updateTenantPrefs(TenantPreferences.assumedOptimizationDwellTime, assumedOptimizationDwellTime);
    }
    if (isTimezoneOfJobStopDirty()) {
      updateTenantPrefs(TenantPreferences.timezoneOfJobStop, timezoneOfJobStop);
    }
    if (isRequireArrAndDepTimesDirty()) {
      updateTenantPrefs(TenantPreferences.requireArrAndDepTimes, requireArrAndDepTimes);
    }
    if (isCanUnassignPartialJobDirty()) {
      updateTenantPrefs(TenantPreferences.unassignPartialJob, canUnassignPartialJob);
    }
    if (isDefaultManifestStatusDirty()) {
      updateTenantPrefs(TenantPreferences.defaultManifestStatus, defaultManifestStatus);
    }
    if (isIdexxCompleteStopLogicDirty()) {
      updateTenantPrefs(TenantPreferences.idexxCompleteStopLogic, idexxCompleteStopLogic);
    }
    if (isAskCheckoutTimeAndMileageDirty()) {
      updateTenantPrefs(TenantPreferences.askCheckoutTimeAndMilage, askCheckoutTimeAndMileage);
    }
    if (isRestrictStationVisibilityDirty()) {
      updateTenantPrefs(TenantPreferences.restrictStationVisibility, restrictStationVisibility);
    }
    if (isCreateManifestOutSideDirty()) {
      updateTenantPrefs(TenantPreferences.createManifestOutSide, createManifestOutSide);
    }
    if (isSkillBasedRequirementsDirty()) {
      updateTenantPrefs(TenantPreferences.skillsBasedRequirements, skillBasedRequirements);
    }
    if (isConsiderSkillsRoutePlanningDirty()) {
      updateTenantPrefs(TenantPreferences.considerSkillsRoutePlanning, considerSkillsRoutePlanning);
    }
    if (isDismissFailedJobsRoutePlanningDirty()) {
      updateTenantPrefs(TenantPreferences.dismissFailedJobsRoutePlanning, dismissFailedJobsRoutePlanning);
    }
    if (isOptimizationObjectivesDirty()) {
      updateTenantPrefs(TenantPreferences.optimizationObjectives, optimizationObjectives);
    }
    if (isStopCompletedAtFutureTimeDirty()) {
      updateTenantPrefs(TenantPreferences.stopCompletedAtFutureTime, stopCompletedAtFutureTime);
    }
    if (isClosedNeverReassignDirty()) {
      updateTenantPrefs(TenantPreferences.closedNeverReassign, closedNeverReassign);
    }
    if (isRoutePlanLimitDirty()) {
      // Stored as { isUnlimited, value }. The cap is always persisted (incl. 0 = no
      // plans) so it is remembered while Unlimited and restored when switching back.
      updateTenantPrefs(TenantPreferences.routePlanningLimit, {
        isUnlimited: routePlanLimitMode === RoutePlanLimitMode.Unlimited,
        value: routePlanningLimit ?? DEFAULT_ROUTE_PLANNING_LIMIT
      });
    }
    if (isSuggestedAssignmentsPerDayDirty()) {
      updateTenantPrefs(TenantPreferences.suggestedAssignmentsPerDay, suggestedAssignmentsPerDay);
    }
  }, [
    isStopCompletedAtFutureTimeDirty,
    isOptimizationObjectivesDirty,
    isEnforcementLevelDirty,
    isOrderEntryTypeDirty,
    isDispatcherIgnoreTimeWindowsDirty,
    isDispatcherIgnoreTimeWindowsOnFirstTryDirty,
    isDispatcherIgnoreTimeWindowsAsDefaultDirty,
    isDriverIgnoreTimeWindowsDirty,
    isDriverAverageDwellTimeDirty,
    isTimezoneOfJobStopDirty,
    isRequireArrAndDepTimesDirty,
    isCanUnassignPartialJobDirty,
    isDefaultManifestStatusDirty,
    isIdexxCompleteStopLogicDirty,
    isAskCheckoutTimeAndMileageDirty,
    isRestrictStationVisibilityDirty,
    isCreateManifestOutSideDirty,
    isSkillBasedRequirementsDirty,
    isDismissFailedJobsRoutePlanningDirty,
    isClosedNeverReassignDirty,
    isRoutePlanLimitDirty,
    isSuggestedAssignmentsPerDayDirty,
    routePlanLimitMode,
    routePlanningLimit,
    suggestedAssignmentsPerDay,
    updateTenantPrefs,
    stopCompletedAtFutureTime,
    optimizationObjectives,
    enforcementLevel,
    orderEntryType,
    isDispatcherIgnoreTimeWindows,
    isDispatcherIgnoreTimeWindowsOnFirstTry,
    isDispatcherIgnoreTimeWindowsAsDefault,
    driverIgnoreTimeWindows,
    assumedOptimizationDwellTime,
    timezoneOfJobStop,
    requireArrAndDepTimes,
    canUnassignPartialJob,
    defaultManifestStatus,
    idexxCompleteStopLogic,
    askCheckoutTimeAndMileage,
    restrictStationVisibility,
    createManifestOutSide,
    skillBasedRequirements,
    isConsiderSkillsRoutePlanningDirty,
    considerSkillsRoutePlanning,
    dismissFailedJobsRoutePlanning,
    closedNeverReassign
  ]);

  const handleCancelSetting = useCallback(() => {
    setEnforcementLevel(initEnforcementLevel as DriverRequirementEnforcementLevel);
    setOrderEntryType(initOrderEntryType as OrderEntryType);
    setDispatcherIgnoreTimeWindows(initDispatcherIgnoreTimeWindows);
    setDispatcherIgnoreTimeWindowsOnFirstTry(initDispatcherIgnoreTimeWindowsOnFirstTry);
    setDispatcherIgnoreTimeWindowsAsDefault(initDispatcherIgnoreTimeWindowsAsDefault);
    setDriverIgnoreTimeWindows(initDriverIgnoreTimeWindows);
    setAssumedOptimizationDwellTime(initAssumedOptimizationDwellTime);
    setTimezoneOfJobStop(initTimezoneOfJobStop);
    setRequireArrAndDepTimes(initRequireArrAndDepTimes);
    setCanUnassignPartialJob(initCanUnassignPartialJob);
    setDefaultManifestStatus(initDefaultManifestStatus);
    setAskCheckoutTimeAndMileage(initAskCheckoutTimeAndMileage);
    setRestrictStationVisibility(initRestrictStationVisibility);
    setCreateManifestOutSide(initCreateManifestOutSide);
    setSkillBasedRequirements(initSkillBasedRequirements);
    setConsiderSkillsRoutePlanning(initConsiderSkillsRoutePlanning);
    setDismissFailedJobsRoutePlanning(initDismissFailedJobsRoutePlanning);
    setOptimizationObjectives(initOptimizationObjectives);
    setStopCompletedAtFutureTime(initStopCompletedAtFutureTime);
    setClosedNeverReassign(initClosedNeverReassign);
    setRoutePlanLimitMode(initRoutePlanLimitMode);
    setRoutePlanningLimit(initRoutePlanningLimit);
    setSuggestedAssignmentsPerDay(initSuggestedAssignmentsPerDay);
  }, [
    initEnforcementLevel,
    initOrderEntryType,
    initDispatcherIgnoreTimeWindows,
    initDispatcherIgnoreTimeWindowsOnFirstTry,
    initDispatcherIgnoreTimeWindowsAsDefault,
    initDriverIgnoreTimeWindows,
    initAssumedOptimizationDwellTime,
    initTimezoneOfJobStop,
    initRequireArrAndDepTimes,
    initCanUnassignPartialJob,
    initDefaultManifestStatus,
    initAskCheckoutTimeAndMileage,
    initRestrictStationVisibility,
    initCreateManifestOutSide,
    initSkillBasedRequirements,
    initConsiderSkillsRoutePlanning,
    initDismissFailedJobsRoutePlanning,
    initOptimizationObjectives,
    initStopCompletedAtFutureTime,
    initClosedNeverReassign,
    initRoutePlanLimitMode,
    initRoutePlanningLimit,
    initSuggestedAssignmentsPerDay
  ]);

  const renderGeneralSettings = useMemo(() => {
    const retVal: ContentProps[] = [];

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Order Entry Version",
      contentOptions: [
        {
          label: "Current",
          value: OrderEntryType.Current
        },
        {
          label: "Legacy",
          value: OrderEntryType.Legacy
        }
      ],
      contentInformation:
        "Sets which version of order entry to use.  The current CSHTML version, or the legacy version.",
      selectedOption: orderEntryType,
      handleChangeOption: handleOrderEntryTypeChange,
      renderPropComponents: renderRadioGroupProps
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Allow Dispatchers to Ignore Optimization Time Window",
      contentOptions: [
        {
          group: "dispatcher-timewindow",
          label: "Yes",
          value: "true"
        },
        {
          group: "dispatcher-timewindow",
          label: "No",
          value: "false"
        }
      ],
      contentInformation:
        "Allow dispatchers to execute route optimizations while ignoring time windows determined by stops.",
      selectedOption: _.toString(isDispatcherIgnoreTimeWindows),
      handleChangeOption: handleDispatcherIgnoreTimeWindowsChange,
      renderPropComponents: renderRadioGroupProps
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Allow dispatchers to ignore time windows on first try",
      contentOptions: [
        {
          group: "dispatcher-timewindow-on-firsttry",
          label: "Yes",
          value: "true"
        },
        {
          group: "dispatcher-timewindow-on-firsttry",
          label: "No",
          value: "false"
        }
      ],
      contentInformation: "Allow dispatchers to ignore time windows on first try when executing route optimizations.",
      selectedOption: _.toString(isDispatcherIgnoreTimeWindowsOnFirstTry),
      handleChangeOption: handleDispatcherIgnoreTimeWindowsOnFirstTryChange,
      renderPropComponents: renderRadioGroupProps,
      isDisabled: () => !isDispatcherIgnoreTimeWindows
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Ignore time windows as default",
      contentOptions: [
        {
          group: "dispatcher-timewindow-as-default",
          label: "Yes",
          value: "true"
        },
        {
          group: "dispatcher-timewindow-as-default",
          label: "No",
          value: "false"
        }
      ],
      contentInformation:
        "Allow dispatchers to ignore time windows on first try by default when executing route optimizations.",
      selectedOption: _.toString(isDispatcherIgnoreTimeWindowsAsDefault),
      handleChangeOption: handleDispatcherIgnoreTimeWindowsAsDefaultChange,
      renderPropComponents: renderRadioGroupProps,
      isDisabled: () => !isDispatcherIgnoreTimeWindows || !isDispatcherIgnoreTimeWindowsOnFirstTry
    });

    retVal.push(
      {
        ...ContentOptionProps,
        contentTitle: "Allow Drivers to Ignore Optimization Time Window",
        contentOptions: [
          {
            group: "driver-timewindow",
            label: "Yes",
            value: "true"
          },
          {
            group: "driver-timewindow",
            label: "No",
            value: "false"
          }
        ],
        contentInformation:
          "Allow drivers to execute route optimizations while ignoring time windows determined by stops.",
        selectedOption: _.toString(driverIgnoreTimeWindows),
        handleChangeOption: handleDriverIgnoreTimeWindowsChange,
        renderPropComponents: renderRadioGroupProps
      },
      {
        ...ContentNumericProp,
        contentTitle: "Assumed Optimization Dwell Time",
        contentInformation: (
          <div>
            Assumed Optimization Dwell Time per stop.
            <br />
            Minimum: {MIN_DWELL_MINUTES} minute, Maximum: {MAX_DWELL_MINUTES} minutes.
          </div>
        ),
        contentUnit: "minutes",
        selectedOption: _.toString(assumedOptimizationDwellTime),
        handleChangeOption: handleAssumedOptimizationDwellTimeChange,
        handleBlur: handleAssumedOptimizationDwellTimeBlur,
        renderPropComponents: renderNumericTextBox
      }
    );

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Time zone of job/stop",
      contentOptions: [
        {
          group: "timezone",
          label: "Yes",
          value: "true"
        },
        {
          group: "timezone",
          label: "No",
          value: "false"
        }
      ],
      contentInformation:
        "Allow show the times of stops and jobs based on the it's timezone. Default: No = Browser's timezone.",
      selectedOption: _.toString(timezoneOfJobStop),
      handleChangeOption: handleTimezoneOfJobStopChange,
      renderPropComponents: renderRadioGroupProps
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Require arrival & departure times",
      contentOptions: [
        {
          group: "ArrivalAndDepartureTimes",
          label: "Yes",
          value: "true"
        },
        {
          group: "ArrivalAndDepartureTimes",
          label: "No",
          value: "false"
        }
      ],
      contentInformation: "The arrival/departure time would be required or not when mark stop as completed.",
      selectedOption: _.toString(requireArrAndDepTimes),
      handleChangeOption: handleRequireArrAndDepTimesChange,
      renderPropComponents: renderRadioGroupProps
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Allow Unassign Partially Complete Job",
      contentInformation: "Allow dispatchers to unassign partially complete jobs.",
      contentOptions: [
        {
          group: "unassignPartialJob",
          label: "Yes",
          value: "true"
        },
        {
          group: "unassignPartialJob",
          label: "No",
          value: "false"
        }
      ],
      handleChangeOption: handleCanUnassignPartialJobChange,
      renderPropComponents: renderRadioGroupProps,
      selectedOption: _.toString(canUnassignPartialJob)
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Default New Manifest Status",
      contentInformation: "The default status when creating new manifest.",
      contentOptions: [
        {
          group: "defaultManifestStatus",
          label: "Active",
          value: ManifestStatusType.Active
        },
        {
          group: "defaultManifestStatus",
          label: "Future",
          value: ManifestStatusType.Future
        },
        {
          group: "defaultManifestStatus",
          label: "Inactive",
          value: ManifestStatusType.Inactive
        }
      ],
      handleChangeOption: handleDefaultManifestStatusChange,
      renderPropComponents: renderRadioGroupProps,
      selectedOption: _.toString(defaultManifestStatus)
    });

    if (idexxFeatureEnabled) {
      retVal.push({
        ...ContentOptionProps,
        contentTitle: "Enable IDEXX Complete Stop Logic",
        contentInformation: "Enable IDEXX Complete Stop Logic.", // TODO: add more information
        contentOptions: [
          {
            group: "idexxCompleteStopLogic",
            label: "Yes",
            value: "true"
          },
          {
            group: "idexxCompleteStopLogic",
            label: "No",
            value: "false"
          }
        ],
        handleChangeOption: handleIdexxCompleteStopLogicChange,
        renderPropComponents: renderRadioGroupProps,
        selectedOption: _.toString(idexxCompleteStopLogic)
      });
    }
    if (askManifestCheckoutInfoEnabled) {
      retVal.push({
        ...ContentOptionProps,
        contentTitle: "Ask for start/end time & mileage when checking out driver",
        contentInformation: "Ask for start/end time & mileage when checking out driver.",
        contentOptions: [
          {
            group: "askCheckoutTimeAndMileage",
            label: "Yes",
            value: "true"
          },
          {
            group: "askCheckoutTimeAndMileage",
            label: "No",
            value: "false"
          }
        ],
        handleChangeOption: handleAskCheckoutTimeAndMilageChange,
        renderPropComponents: renderRadioGroupProps,
        selectedOption: _.toString(askCheckoutTimeAndMileage)
      });
    }

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Restrict Station Visibility",
      contentInformation: "Restrict Station Visibility.",
      contentOptions: [
        {
          group: "restrictStationVisibility",
          label: "Yes",
          value: "true"
        },
        {
          group: "restrictStationVisibility",
          label: "No",
          value: "false"
        }
      ],
      handleChangeOption: handleRestrictStationVisibilityChange,
      renderPropComponents: renderRadioGroupProps,
      selectedOption: _.toString(restrictStationVisibility)
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Create Manifests Outside Selected Driver Group(s)",
      contentInformation: "Allow create manifests outside selected driver Group(s).",
      contentOptions: [
        {
          group: "createManifestOutSide",
          label: "Yes",
          value: "true"
        },
        {
          group: "createManifestOutSide",
          label: "No",
          value: "false"
        }
      ],
      handleChangeOption: handleCreateManifestOutSideChange,
      renderPropComponents: renderRadioGroupProps,
      selectedOption: _.toString(createManifestOutSide)
    });

    if (routePlanningEnabled) {
      retVal.push({
        ...ContentOptionProps,
        contentTitle: "Allow Dispatcher to dismiss failed jobs in Itinerary Planning",
        contentInformation: "Allow dispatchers to dismiss failed jobs in Itinerary Planning.",
        contentOptions: [
          {
            group: "dismissFailedJobsRoutePlanning",
            label: "Yes",
            value: "true"
          },
          {
            group: "dismissFailedJobsRoutePlanning",
            label: "No",
            value: "false"
          }
        ],
        handleChangeOption: handleDismissFailedJobsRoutePlanningChange,
        renderPropComponents: renderRadioGroupProps,
        selectedOption: _.toString(dismissFailedJobsRoutePlanning)
      });

      if (itineraryPlanningSkillsEnabled) {
        retVal.push({
          ...ContentOptionProps,
          contentTitle: "Consider Skills in Itinerary Planning",
          contentInformation:
            "When enabled, only assign jobs to drivers who have all necessary skills during the initial route calculation.",
          contentOptions: [
            {
              group: "considerSkillsRoutePlanning",
              label: "Yes",
              value: "true"
            },
            {
              group: "considerSkillsRoutePlanning",
              label: "No",
              value: "false"
            }
          ],
          selectedOption: _.toString(considerSkillsRoutePlanning),
          handleChangeOption: handleConsiderSkillsRoutePlanningChange,
          renderPropComponents: renderRadioGroupProps
        });
      }

      retVal.push({
        ...ContentOptionProps,
        contentTitle: "Optimization objectives available to the dispatcher",
        contentOptions: [
          {
            group: "optimizationObjectives",
            label: "Minimize time to complete the work",
            value: OptimizationObjective.MinCompletionTime
          },
          {
            group: "optimizationObjectives",
            label: "Divide work as evenly as possible based on time working",
            value: OptimizationObjective.BalanceWorks
          },
          {
            group: "optimizationObjectives",
            label: "Both",
            value: OptimizationObjective.Both
          }
        ],
        contentInformation:
          "If 'Both' is selected, these options will be available for the Dispatcher in the Itinerary Planning flow. If one is selected, it will assume this is the goal.",
        selectedOption: optimizationObjectives,
        handleChangeOption: handleOptimizationObjectivesChange,
        renderPropComponents: renderRadioGroupProps
      });

      // Freemium weekly itinerary-plan limit (DP-3062) — internal e-Courier admins only.
      if (isSuperUser) {
        retVal.push({
          ...ContentOptionProps,
          contentTitle: "Itinerary Plan Weekly Limit",
          contentInformation:
            "Limit how many itinerary plans this company can run per week (Mon–Sun, resets 12:01 AM EST/EDT). Internal admins only.",
          contentOptions: [
            {
              group: "routePlanLimitMode",
              label: "Unlimited",
              value: RoutePlanLimitMode.Unlimited
            },
            {
              group: "routePlanLimitMode",
              label: "Limited",
              value: RoutePlanLimitMode.Limited
            }
          ],
          selectedOption: routePlanLimitMode,
          handleChangeOption: handleRoutePlanLimitModeChange,
          renderPropComponents: renderRadioGroupProps
        });

        retVal.push({
          ...ContentNumericProp,
          contentTitle: "Plans per week",
          contentInformation: (
            <div>
              Maximum number of itinerary plans this company can run each week.
              <br />
              Set to 0 to block all itinerary planning.
            </div>
          ),
          contentUnit: "plans",
          selectedOption: _.toString(routePlanningLimit),
          handleChangeOption: handleRoutePlanningLimitChange,
          renderPropComponents: renderNumericTextBox,
          isDisabled: () => routePlanLimitMode !== RoutePlanLimitMode.Limited
        });
      }
    }

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Allow Marking Stop Complete at Future Time",
      contentOptions: [
        {
          group: "stopCompletedAtFutureTime",
          label: "Yes",
          value: "true"
        },
        {
          group: "stopCompletedAtFutureTime",
          label: "No",
          value: "false"
        }
      ],
      contentInformation: "Allow marking stop complete at future time.",
      selectedOption: _.toString(stopCompletedAtFutureTime),
      handleChangeOption: handleStopCompletedAtFutureTimeChange,
      renderPropComponents: renderRadioGroupProps
    });

    if (closedNeverReassignEnabled) {
      retVal.push({
        ...ContentOptionProps,
        contentTitle: "Prevent jobs that were ever complete from being reassigned or unassigned",
        contentInformation: "Prevent jobs that were ever complete from being reassigned or unassigned.",
        contentOptions: [
          {
            group: "closedNeverReassign",
            label: "Yes",
            value: "true"
          },
          {
            group: "closedNeverReassign",
            label: "No",
            value: "false"
          }
        ],
        handleChangeOption: handleClosedNeverReassignChange,
        renderPropComponents: renderRadioGroupProps,
        selectedOption: _.toString(closedNeverReassign)
      });
    }

    // DP-3194: suggested assignment daily cap
    if (suggestedAssignmentEnabled) {
      retVal.push({
        ...ContentNumericProp,
        contentTitle: "Number of Suggested Assignments per Day",
        contentInformation: (
          <div>
            Maximum number of suggested assignments this company can run each day.
            <br />
            Set to 0 to block all suggested assignments.
            <br />
            Resets daily at 12:01 AM Eastern.
          </div>
        ),
        contentUnit: "suggestions",
        selectedOption: _.toString(suggestedAssignmentsPerDay),
        handleChangeOption: handleSuggestedAssignmentsPerDayChange,
        renderPropComponents: renderNumericTextBox
      });
    }

    return retVal;
  }, [
    orderEntryType,
    handleOrderEntryTypeChange,
    isDispatcherIgnoreTimeWindows,
    handleDispatcherIgnoreTimeWindowsChange,
    isDispatcherIgnoreTimeWindowsOnFirstTry,
    handleDispatcherIgnoreTimeWindowsOnFirstTryChange,
    isDispatcherIgnoreTimeWindowsAsDefault,
    handleDispatcherIgnoreTimeWindowsAsDefaultChange,
    driverIgnoreTimeWindows,
    handleDriverIgnoreTimeWindowsChange,
    assumedOptimizationDwellTime,
    handleAssumedOptimizationDwellTimeChange,
    handleAssumedOptimizationDwellTimeBlur,
    timezoneOfJobStop,
    handleTimezoneOfJobStopChange,
    requireArrAndDepTimes,
    handleRequireArrAndDepTimesChange,
    handleCanUnassignPartialJobChange,
    canUnassignPartialJob,
    handleDefaultManifestStatusChange,
    defaultManifestStatus,
    idexxFeatureEnabled,
    askManifestCheckoutInfoEnabled,
    handleCreateManifestOutSideChange,
    createManifestOutSide,
    routePlanningEnabled,
    handleIdexxCompleteStopLogicChange,
    idexxCompleteStopLogic,
    handleAskCheckoutTimeAndMilageChange,
    askCheckoutTimeAndMileage,
    handleRestrictStationVisibilityChange,
    restrictStationVisibility,
    handleDismissFailedJobsRoutePlanningChange,
    dismissFailedJobsRoutePlanning,
    optimizationObjectives,
    handleOptimizationObjectivesChange,
    stopCompletedAtFutureTime,
    handleStopCompletedAtFutureTimeChange,
    closedNeverReassignEnabled,
    closedNeverReassign,
    handleClosedNeverReassignChange,
    isSuperUser,
    routePlanLimitMode,
    handleRoutePlanLimitModeChange,
    routePlanningLimit,
    handleRoutePlanningLimitChange,
    itineraryPlanningSkillsEnabled,
    considerSkillsRoutePlanning,
    handleConsiderSkillsRoutePlanningChange,
    suggestedAssignmentEnabled,
    suggestedAssignmentsPerDay,
    handleSuggestedAssignmentsPerDayChange
  ]);

  const renderDriverRequirementSettings = useMemo(() => {
    const driverRequirementsSettings: ContentProps[] = [];
    if (skillsEnabled) {
      driverRequirementsSettings.push({
        ...ContentOptionProps,
        contentTitle: "Use Skill Based Requirements",
        contentInformation: "Use Skill Based Requirements.",
        contentOptions: [
          {
            group: "skillBasedRequirements",
            label: "Yes",
            value: "true"
          },
          {
            group: "skillBasedRequirements",
            label: "No",
            value: "false"
          }
        ],
        selectedOption: _.toString(skillBasedRequirements),
        handleChangeOption: handleSkillBasedRequirementsChange,
        renderPropComponents: renderRadioGroupProps
      });
    }
    driverRequirementsSettings.push({
      ...ContentOptionProps,
      contentTitle: "Assignment to non-qualified drivers",
      contentOptions: [
        {
          label: "Always allow",
          value: DriverRequirementEnforcementLevel.Allow
        },
        {
          label: "Allow with confirmation",
          value: DriverRequirementEnforcementLevel.Warning
        },
        {
          label: "Never allow",
          value: DriverRequirementEnforcementLevel.Prevent
        }
      ],
      contentInformation: "Assignment of the jobs with driver requirements to the drivers with missing requirements.",
      selectedOption: enforcementLevel,
      handleChangeOption: handleDriverRequiredChange
    });
    return driverRequirementsSettings;
  }, [
    enforcementLevel,
    handleDriverRequiredChange,
    skillsEnabled,
    skillBasedRequirements,
    handleSkillBasedRequirementsChange
  ]);

  const handleEnforcementLevel = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<DriverRequirementEnforcementLevel> = extractSimplePref(
      prefs,
      TenantPreferences.driverRequirementEnforcement,
      DriverRequirementEnforcementLevel.Warning
    );
    setEnforcementLevel(prefVal.value as DriverRequirementEnforcementLevel);
    setInitEnforcementLevel(prefVal.value as DriverRequirementEnforcementLevel);
  }, []);

  const handleSkillBasedRequirements = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.skillsBasedRequirements, false);
    setSkillBasedRequirements(prefVal.value as boolean);
    setInitSkillBasedRequirements(prefVal.value as boolean);
  }, []);

  const handleConsiderSkillsRoutePlanning = useCallback((prefs: Pref[]) => {
    // DP-3166: unset companies default to "Yes"
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.considerSkillsRoutePlanning, true);
    setConsiderSkillsRoutePlanning(prefVal.value as boolean);
    setInitConsiderSkillsRoutePlanning(prefVal.value as boolean);
  }, []);

  const handleOrderEntryType = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<OrderEntryType> = extractSimplePref(
      prefs,
      TenantPreferences.orderEntryType,
      OrderEntryType.Current
    );
    setOrderEntryType(prefVal.value as OrderEntryType);
    setInitOrderEntryType(prefVal.value as OrderEntryType);
  }, []);

  const handleDispatcherIgnoreTimeWindows = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.dispatcherIgnoreTimeWindows, false);
    setDispatcherIgnoreTimeWindows(prefVal.value as boolean);
    setInitDispatcherIgnoreTimeWindows(prefVal.value as boolean);
  }, []);

  const handleDispatcherIgnoreTimeWindowsOnFirstTry = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(
      prefs,
      TenantPreferences.dispatcherIgnoreTimeWindowsOnFirstTry,
      true
    );
    setDispatcherIgnoreTimeWindowsOnFirstTry(prefVal.value as boolean);
    setInitDispatcherIgnoreTimeWindowsOnFirstTry(prefVal.value as boolean);
  }, []);

  const handleDispatcherIgnoreTimeWindowsAsDefault = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(
      prefs,
      TenantPreferences.dispatcherIgnoreTimeWindowsAsDefault,
      false
    );
    setDispatcherIgnoreTimeWindowsAsDefault(prefVal.value as boolean);
    setInitDispatcherIgnoreTimeWindowsAsDefault(prefVal.value as boolean);
  }, []);

  const handleDriverIgnoreTimeWindows = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.driverIgnoreTimeWindows, false);
    setDriverIgnoreTimeWindows(prefVal.value as boolean);
    setInitDriverIgnoreTimeWindows(prefVal.value as boolean);
  }, []);

  const handleAssumedOptimizationDwellTime = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<number> = extractSimplePref(
      prefs,
      TenantPreferences.assumedOptimizationDwellTime,
      DEFAULT_DWELL_MINUTES
    );
    const value = Math.min(prefVal.value as number, MAX_DWELL_MINUTES);
    setAssumedOptimizationDwellTime(value);
    setInitAssumedOptimizationDwellTime(value);
  }, []);

  const handleTimezoneOfJobStop = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.timezoneOfJobStop, false);
    setTimezoneOfJobStop(prefVal.value as boolean);
    setInitTimezoneOfJobStop(prefVal.value as boolean);
  }, []);

  const handleRequireArrAndDepTimes = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.requireArrAndDepTimes, false);
    setRequireArrAndDepTimes(prefVal.value as boolean);
    setInitRequireArrAndDepTimes(prefVal.value as boolean);
  }, []);

  const handleCanUnassignPartialJob = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.unassignPartialJob, false);
    setCanUnassignPartialJob(prefVal.value as boolean);
    setInitCanUnassignPartialJob(prefVal.value as boolean);
  }, []);

  const handleDefaultManifestStatus = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<ManifestStatusType> = extractSimplePref(
      prefs,
      TenantPreferences.defaultManifestStatus,
      ManifestStatusType.Active
    );
    setDefaultManifestStatus(prefVal.value as ManifestStatusType);
    setInitDefaultManifestStatus(prefVal.value as ManifestStatusType);
  }, []);

  const handleIdexxCompleteStopLogic = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.idexxCompleteStopLogic, false);
    setIdexxCompleteStopLogic(prefVal.value as boolean);
    setInitIdexxCompleteStopLogic(prefVal.value as boolean);
  }, []);

  const handleAskCheckoutTimeAndMilage = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.askCheckoutTimeAndMilage, false);
    setAskCheckoutTimeAndMileage(prefVal.value as boolean);
    setInitAskCheckoutTimeAndMileage(prefVal.value as boolean);
  }, []);

  const handleRestrictStationVisibility = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.restrictStationVisibility, false);
    setRestrictStationVisibility(prefVal.value as boolean);
    setInitRestrictStationVisibility(prefVal.value as boolean);
  }, []);

  const handleCreateManifestOutSide = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.createManifestOutSide, false);
    setCreateManifestOutSide(prefVal.value as boolean);
    setInitCreateManifestOutSide(prefVal.value as boolean);
  }, []);

  const handleDismissFailedJobsRoutePlanning = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(
      prefs,
      TenantPreferences.dismissFailedJobsRoutePlanning,
      true // DP-3166: unset companies default to "Yes"
    );
    setDismissFailedJobsRoutePlanning(prefVal.value as boolean);
    setInitDismissFailedJobsRoutePlanning(prefVal.value as boolean);
  }, []);

  const handleOptimizationObjectives = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<OptimizationObjective> = extractSimplePref(
      prefs,
      TenantPreferences.optimizationObjectives,
      OptimizationObjective.Both // DP-3113: unset companies default to "Both"
    );
    setOptimizationObjectives(prefVal.value as OptimizationObjective);
    setInitOptimizationObjectives(prefVal.value as OptimizationObjective);
  }, []);

  const handleStopCompletedAtFutureTime = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.stopCompletedAtFutureTime, false);
    setStopCompletedAtFutureTime(prefVal.value as boolean);
    setInitStopCompletedAtFutureTime(prefVal.value as boolean);
  }, []);

  const handleClosedNeverReassign = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.closedNeverReassign, false);
    setClosedNeverReassign(prefVal.value as boolean);
    setInitClosedNeverReassign(prefVal.value as boolean);
  }, []);

  const handleRoutePlanningLimit = useCallback((prefs: Pref[]) => {
    // Stored as { isUnlimited, value }. Unlimited is the explicit flag; the cap is
    // always loaded (defaulting to Limited 2 when absent) so it is shown/remembered
    // regardless of mode. 0 is a valid cap (no plans).
    const pref = extractSimplePref<{isUnlimited?: boolean; value?: number}>(
      prefs,
      TenantPreferences.routePlanningLimit,
      {
        isUnlimited: false,
        value: DEFAULT_ROUTE_PLANNING_LIMIT
      }
    ).value;
    const capValue = Number(pref?.value);
    const cap = Number.isFinite(capValue) && capValue >= 0 ? capValue : DEFAULT_ROUTE_PLANNING_LIMIT;
    const mode = pref?.isUnlimited ? RoutePlanLimitMode.Unlimited : RoutePlanLimitMode.Limited;

    setRoutePlanLimitMode(mode);
    setInitRoutePlanLimitMode(mode);
    setRoutePlanningLimit(cap);
    setInitRoutePlanningLimit(cap);
  }, []);

  // DP-3194: load suggested assignment daily cap
  const handleSuggestedAssignmentsPerDay = useCallback((prefs: Pref[]) => {
    const prefVal = extractSimplePref<number>(
      prefs,
      TenantPreferences.suggestedAssignmentsPerDay,
      DEFAULT_SUGGESTED_ASSIGNMENTS_PER_DAY
    );
    const val = Number(prefVal.value);
    const capped =
      Number.isFinite(val) && val >= MIN_SUGGESTED_ASSIGNMENTS_PER_DAY ? val : DEFAULT_SUGGESTED_ASSIGNMENTS_PER_DAY;
    setSuggestedAssignmentsPerDay(capped);
    setInitSuggestedAssignmentsPerDay(capped);
  }, []);

  useEffect(() => {
    log.debug("Tenant preferences loaded: ", tenantPreferences);
    handleEnforcementLevel(tenantPreferences);
    handleOrderEntryType(tenantPreferences);
    handleDispatcherIgnoreTimeWindows(tenantPreferences);
    handleDispatcherIgnoreTimeWindowsOnFirstTry(tenantPreferences);
    handleDispatcherIgnoreTimeWindowsAsDefault(tenantPreferences);
    handleDriverIgnoreTimeWindows(tenantPreferences);
    handleAssumedOptimizationDwellTime(tenantPreferences);
    handleTimezoneOfJobStop(tenantPreferences);
    handleRequireArrAndDepTimes(tenantPreferences);
    handleCanUnassignPartialJob(tenantPreferences);
    handleDefaultManifestStatus(tenantPreferences);
    handleIdexxCompleteStopLogic(tenantPreferences);
    handleAskCheckoutTimeAndMilage(tenantPreferences);
    handleRestrictStationVisibility(tenantPreferences);
    handleCreateManifestOutSide(tenantPreferences);
    handleSkillBasedRequirements(tenantPreferences);
    handleConsiderSkillsRoutePlanning(tenantPreferences);
    handleDismissFailedJobsRoutePlanning(tenantPreferences);
    handleOptimizationObjectives(tenantPreferences);
    handleStopCompletedAtFutureTime(tenantPreferences);
    handleClosedNeverReassign(tenantPreferences);
    handleRoutePlanningLimit(tenantPreferences);
    handleSuggestedAssignmentsPerDay(tenantPreferences);
  }, [
    handleCanUnassignPartialJob,
    handleDispatcherIgnoreTimeWindows,
    handleDispatcherIgnoreTimeWindowsOnFirstTry,
    handleDispatcherIgnoreTimeWindowsAsDefault,
    handleAssumedOptimizationDwellTime,
    handleDriverIgnoreTimeWindows,
    handleEnforcementLevel,
    handleOrderEntryType,
    handleRequireArrAndDepTimes,
    handleTimezoneOfJobStop,
    handleDefaultManifestStatus,
    handleIdexxCompleteStopLogic,
    tenantPreferences,
    handleAskCheckoutTimeAndMilage,
    handleRestrictStationVisibility,
    handleCreateManifestOutSide,
    handleSkillBasedRequirements,
    handleConsiderSkillsRoutePlanning,
    handleDismissFailedJobsRoutePlanning,
    handleOptimizationObjectives,
    handleStopCompletedAtFutureTime,
    handleClosedNeverReassign,
    handleRoutePlanningLimit,
    handleSuggestedAssignmentsPerDay
  ]);

  useEffect(() => {
    const data = setTenantPrefData?.setTenantPref as UserPref;
    log.debug("Pref saved: ", data);
    if (data && data.name === TenantPreferences.orderEntryType) {
      handleOrderEntryType([data]);
    }
    if (data && data.name === TenantPreferences.driverRequirementEnforcement) {
      handleEnforcementLevel([data]);
    }
    if (data && data.name === TenantPreferences.dispatcherIgnoreTimeWindows) {
      handleDispatcherIgnoreTimeWindows([data]);
    }
    if (data && data.name === TenantPreferences.dispatcherIgnoreTimeWindowsOnFirstTry) {
      handleDispatcherIgnoreTimeWindowsOnFirstTry([data]);
    }
    if (data && data.name === TenantPreferences.dispatcherIgnoreTimeWindowsAsDefault) {
      handleDispatcherIgnoreTimeWindowsAsDefault([data]);
    }
    if (data && data.name === TenantPreferences.driverIgnoreTimeWindows) {
      handleDriverIgnoreTimeWindows([data]);
    }
    if (data && data.name === TenantPreferences.assumedOptimizationDwellTime) {
      handleAssumedOptimizationDwellTime([data]);
    }
    if (data && data.name === TenantPreferences.timezoneOfJobStop) {
      handleTimezoneOfJobStop([data]);
    }
    if (data && data.name === TenantPreferences.requireArrAndDepTimes) {
      handleRequireArrAndDepTimes([data]);
    }
    if (data && data.name === TenantPreferences.unassignPartialJob) {
      handleCanUnassignPartialJob([data]);
    }
    if (data && data.name === TenantPreferences.defaultManifestStatus) {
      handleDefaultManifestStatus([data]);
    }
    if (data && data.name === TenantPreferences.askCheckoutTimeAndMilage) {
      handleAskCheckoutTimeAndMilage([data]);
    }
    if (data && data.name === TenantPreferences.restrictStationVisibility) {
      handleRestrictStationVisibility([data]);
    }
    if (data && data.name === TenantPreferences.createManifestOutSide) {
      handleCreateManifestOutSide([data]);
    }
    if (data && data.name === TenantPreferences.skillsBasedRequirements) {
      handleSkillBasedRequirements([data]);
    }
    if (data && data.name === TenantPreferences.considerSkillsRoutePlanning) {
      handleConsiderSkillsRoutePlanning([data]);
    }
    if (data && data.name === TenantPreferences.dismissFailedJobsRoutePlanning) {
      handleDismissFailedJobsRoutePlanning([data]);
    }
    if (data && data.name === TenantPreferences.optimizationObjectives) {
      handleOptimizationObjectives([data]);
    }
    if (data && data.name === TenantPreferences.stopCompletedAtFutureTime) {
      handleStopCompletedAtFutureTime([data]);
    }
    if (data && data.name === TenantPreferences.closedNeverReassign) {
      handleClosedNeverReassign([data]);
    }
    if (data && data.name === TenantPreferences.routePlanningLimit) {
      handleRoutePlanningLimit([data]);
    }
    if (data && data.name === TenantPreferences.suggestedAssignmentsPerDay) {
      handleSuggestedAssignmentsPerDay([data]);
    }
  }, [
    handleEnforcementLevel,
    handleOrderEntryType,
    handleDispatcherIgnoreTimeWindows,
    setTenantPrefData?.setTenantPref,
    handleDriverIgnoreTimeWindows,
    handleAssumedOptimizationDwellTime,
    handleTimezoneOfJobStop,
    handleRequireArrAndDepTimes,
    handleCanUnassignPartialJob,
    handleDefaultManifestStatus,
    handleAskCheckoutTimeAndMilage,
    handleRestrictStationVisibility,
    handleDispatcherIgnoreTimeWindowsOnFirstTry,
    handleDispatcherIgnoreTimeWindowsAsDefault,
    handleCreateManifestOutSide,
    handleSkillBasedRequirements,
    handleConsiderSkillsRoutePlanning,
    handleDismissFailedJobsRoutePlanning,
    handleOptimizationObjectives,
    handleStopCompletedAtFutureTime,
    handleClosedNeverReassign,
    handleRoutePlanningLimit,
    handleSuggestedAssignmentsPerDay
  ]);

  return (
    <Container>
      <Header>
        <HeaderText>Company Settings</HeaderText>
      </Header>
      <Body>
        <Section
          sectionName="general-settings-section"
          sectionTitle="General Settings"
          render={renderGeneralSettings}
        />
        {driverRequirementsEnabled && (
          <Section
            sectionName="driver-requirements-section"
            sectionTitle="Driver Requirement Settings"
            render={renderDriverRequirementSettings}
          />
        )}
      </Body>
      <Footer>
        <CancelButton
          data-testid="cancel-button"
          disabled={!isSaveChange() || isSetTenantPrefLoading}
          onClick={handleCancelSetting}
          intent="none"
          outlined
        >
          Cancel
        </CancelButton>
        <SaveButton
          data-testid="save-button"
          disabled={!isSaveChange() || isSetTenantPrefLoading}
          onClick={handleSaveSetting}
          intent="primary"
        >
          Save
        </SaveButton>
      </Footer>
    </Container>
  );
};

export default General;
